/**
 * Schedule Demo Form Modal
 * Handles form submission via Formspree and modal interactions
 */

export function initDemoForm() {
  const modalElement = document.getElementById('scheduleDemoModal')
  const formElement = document.getElementById('scheduleDemoForm') as HTMLFormElement
  const formStatus = document.getElementById('formStatus')

  if (!modalElement || !formElement || !formStatus) return

  const closeButton = modalElement.querySelector('.modal-close')
  const dismissButton = modalElement.querySelector('.modal-dismiss')
  const submitButton = formElement.querySelector('button[type="submit"]')

  // Show modal function
  function showModal() {
    modalElement!.style.display = 'flex'
    // Trigger reflow to enable transition
    modalElement!.offsetHeight
    modalElement!.classList.add('active')
    document.body.style.overflow = 'hidden'
  }

  // Hide modal function
  function hideModal() {
    modalElement!.classList.remove('active')
    document.body.style.overflow = ''
    setTimeout(() => {
      modalElement!.style.display = 'none'
      // Reset form and status after modal is hidden
      resetForm()
    }, 300) // Match transition duration
  }

  // Reset form and status messages
  function resetForm() {
    formElement!.reset()
    formStatus!.style.display = 'none'
    formStatus!.className = 'form-status'
    formStatus!.textContent = ''
    // Show buttons again
    const modalActions = formElement!.querySelector('.modal-actions') as HTMLElement
    if (modalActions) modalActions.style.display = 'flex'
  }

  // Show status message
  function showStatus(message: string, type: 'success' | 'error') {
    formStatus!.textContent = message
    formStatus!.className = `form-status form-status--${type}`
    formStatus!.style.display = 'block'

    // Hide buttons on success
    if (type === 'success') {
      const modalActions = formElement!.querySelector('.modal-actions') as HTMLElement
      if (modalActions) modalActions.style.display = 'none'
    }
  }

  // Show loading state
  function setLoading(loading: boolean) {
    if (loading) {
      submitButton?.classList.add('loading')
      submitButton?.setAttribute('disabled', 'true')
    } else {
      submitButton?.classList.remove('loading')
      submitButton?.removeAttribute('disabled')
    }
  }

  // Open modal when clicking Schedule Demo buttons/links
  document.querySelectorAll('.schedule-demo-trigger, a[href="#schedule-demo"]').forEach(trigger => {
    trigger.addEventListener('click', function(e) {
      e.preventDefault()
      showModal()
    })
  })

  // Also handle .contact-email links to open the form modal instead of mailto
  document.querySelectorAll('.contact-email').forEach(link => {
    link.addEventListener('click', function(e) {
      e.preventDefault()
      showModal()
    })
  })

  // Close modal on close button click
  closeButton?.addEventListener('click', hideModal)

  // Close modal on dismiss button click
  dismissButton?.addEventListener('click', hideModal)

  // Close modal when clicking overlay background
  modalElement.addEventListener('click', function(e) {
    if (e.target === modalElement) {
      hideModal()
    }
  })

  // Close modal on Escape key
  document.addEventListener('keydown', function(e) {
    if (e.key === 'Escape' && modalElement.classList.contains('active')) {
      hideModal()
    }
  })

  // Handle form submission
  formElement.addEventListener('submit', async function(e) {
    e.preventDefault()

    // Get form data
    const formData = new FormData(formElement)

    // Basic client-side validation
    const name = formData.get('name') as string
    const company = formData.get('company') as string
    const email = formData.get('email') as string

    if (!name || !company || !email) {
      showStatus('Please fill in all required fields.', 'error')
      return
    }

    // Email validation
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
    if (!emailRegex.test(email)) {
      showStatus('Please enter a valid email address.', 'error')
      return
    }

    // Check honeypot field (spam protection)
    const website = formData.get('website')
    if (website) {
      // Bot filled the honeypot field, silently reject
      showStatus('Thank you! We\'ll be in touch soon.', 'success')
      setTimeout(() => {
        hideModal()
      }, 2000)
      return
    }

    // Show loading state
    setLoading(true)
    formStatus!.style.display = 'none'

    try {
      // Submit to Formspree
      const response = await fetch(formElement.action, {
        method: 'POST',
        body: formData,
        headers: {
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        // Success
        showStatus('Thank you! We\'ve received your request and will be in touch within 24 hours.', 'success')
        setTimeout(() => {
          hideModal()
        }, 3000)
      } else {
        // Formspree returned an error
        const data = await response.json()
        const errorMessage = data.errors?.map((err: { message: string }) => err.message).join(', ') || 'Something went wrong. Please try again.'
        showStatus(errorMessage, 'error')
      }
    } catch (error) {
      // Network error
      showStatus('Failed to send request. Please check your connection and try again.', 'error')
      console.error('Form submission error:', error)
    } finally {
      setLoading(false)
    }
  })
}
